import React, { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Formik } from 'formik';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Typography } from '@material-ui/core';

import {
  SegmentEventType,
  UpdateUserProfileMutation,
  UpdateUserProfileMutationVariables,
  useGetAllClientDepartmentsQuery,
  useGetAllClientLocationsQuery,
  useUpdateUserProfileMutation,
} from 'api';
import { ClientLogo, Layouts, Loaders, PaddedPaper } from 'components';
import { SingleDepartmentPicker, SingleLocationPicker } from 'components/Pickers';
import { useCurrentUser, useNotify, useProtectedClient } from 'hooks';
import { useUpdateCurrentUser } from 'updaters';
import { getAppImage } from 'helpers';
import * as paths from 'paths';

import { OnboardingIdentitySchema, OnboardingIdentityValues } from '../validations';

import { useTrackingQuery } from 'app-react-query/hooks';
import { AccountMarker } from 'features/header';
import { isEmpty } from 'lodash';

type Update = UpdateUserProfileMutation;
type UpdateVariables = UpdateUserProfileMutationVariables;

const OnboardingIdentity: FC = () => {
  const navigate = useNavigate();
  const client = useProtectedClient();
  const currentUser = useCurrentUser();
  const { t } = useTranslation();
  const notify = useNotify();

  const { locations_in_signup_required, departments_in_signup_required } = client;
  const { department_id, location_id } = currentUser;

  const updateOptions = useUpdateCurrentUser<Update, UpdateVariables>();
  const initialValues: OnboardingIdentityValues = {
    department_id: department_id || '',
    location_id: location_id || '',
  };

  const track = useTrackingQuery();

  const { mutate, isLoading } = useUpdateUserProfileMutation({
    onSuccess: data => {
      const newUserProfile = data.updateUserProfile;
      track(
        SegmentEventType.SignupLoginNewUserSignupClientUserModifiedSelectDeptloc,
        newUserProfile?.id,
        {
          user_department: newUserProfile?.department_title,
          user_location: newUserProfile?.location_title,
        }
      );
      navigate(paths.onboardingSocials);
    },
    ...updateOptions,
  });

  const { data: departmentData, isLoading: isDepartmentsLoading } = useGetAllClientDepartmentsQuery(
    { data: {} },
    { onError: notify.queryError }
  );

  const { data: locationData, isLoading: isLocationsLoading } = useGetAllClientLocationsQuery(
    { data: {} },
    { onError: notify.queryError }
  );

  useEffect(() => {
    if (!isDepartmentsLoading && !isLocationsLoading) {
      if (
        !isEmpty(locationData?.getAllClientLocations?.client_locations) &&
        !isEmpty(departmentData?.getAllClientDepartments?.client_departments)
      ) {
        return;
      }

      navigate(paths.onboardingSocials);
    }
  }, [isLocationsLoading, isDepartmentsLoading]);

  function handleSubmit(values: OnboardingIdentityValues) {
    mutate({
      data: {
        id: currentUser.id,
        last_onboarding_step: 2,
        location_id: values.location_id || '',
        department_id: values.department_id || '',
      },
    });
  }

  function renderImage() {
    return <img width={500} height={500} {...getAppImage('illustrations/browser.png', 500)} />;
  }

  function renderMain() {
    if (isLocationsLoading && isDepartmentsLoading) {
      return <Loaders.Standard />;
    }

    return (
      <PaddedPaper elevation={0} padding="64px 48px">
        <Formik
          validateOnBlur={false}
          validateOnChange={false}
          onSubmit={handleSubmit}
          initialValues={initialValues}
          validationSchema={OnboardingIdentitySchema(client)}
        >
          <Layouts.OnboardingBox component={Form}>
            <Box textAlign="center">
              <ClientLogo client={client} />
            </Box>
            <Typography align="center" paragraph variant="h2">
              {t('onboarding:identityHeader')}
            </Typography>
            <Typography align="center" paragraph color="textSecondary">
              {t('onboarding:identitySubHead')}
            </Typography>
            <Box paddingTop={4}>
              <SingleDepartmentPicker disableClearable={departments_in_signup_required} />
            </Box>
            <Box paddingTop={4}>
              <SingleLocationPicker disableClearable={locations_in_signup_required} />
            </Box>
            <Box paddingTop={5}>
              <Button
                fullWidth
                type="submit"
                size="large"
                variant="contained"
                color="primary"
                disabled={isLoading}
              >
                {isLoading ? <Loaders.Standard height={26} /> : t('auth:letsGo')}
              </Button>
            </Box>
            <AccountMarker />
          </Layouts.OnboardingBox>
        </Formik>
      </PaddedPaper>
    );
  }

  return <Layouts.Onboarding image={renderImage()} main={renderMain()} />;
};

export default OnboardingIdentity;
